'use client';
import { getAlignment, getSectionProps } from 'utils/styles';
import NotificationOverlay from 'components/shared/NotificationOverlay/NotificationOverlay';
import BadgeOverlay from 'components/shared/BadgeOverlay/BadgeOverlay';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import type { Color } from 'libs/contentful/types';
import { MediaCopyProps } from './types';
import Media from 'components/shared/Media/Media';
import {
  CopyJustification,
  CopyPosition,
  Margins,
  MediaCopyVariants,
  NotificationOverlayDesktopPosition,
  Alignment,
  OverflowVisibleSettings,
} from 'types/constants';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';
import useCarousel from 'hooks/useCarousel';
import AutoHeight from 'embla-carousel-auto-height';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import CarouselFooter from 'components/shared/Carousel/CarouselFooter';
import MediaCopyCopyItem from './MediaCopyCopyItem';
import AutoPlayCarousel from 'components/shared/Carousel/AutoPlayCarousel';
import Eyebrow from 'components/shared/Eyebrow/Eyebrow';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { calculateOverlayMargin } from 'utils/styles';
import { cn } from 'utils/styles';
import HyperlinkedMedia from '../shared/HyperlinkedMedia/HyperlinkedMedia';
import { VideoPlayer, VideoPreview, VideoControls } from '../VideoPlayer';

const getInnerOuterSectionProps = (
  isContainedBackground: boolean,
  color: Color,
  containedColor: Color | undefined,
  margins: Margins | undefined,
  sectionName: string | undefined,
) => {
  // For Contained Background variant: outer wrapper gets color/margins/sectionName, inner section gets containedColor
  // For other variants: section gets color/margins/sectionName
  return {
    outer: isContainedBackground ? getSectionProps(color, margins, sectionName) : {},
    inner: getSectionProps(
      isContainedBackground ? containedColor : color,
      isContainedBackground ? undefined : margins,
      isContainedBackground ? undefined : sectionName,
      cn(isContainedBackground && 'rounded-lg py-12 sm:px-20 sm:py-40'),
    ),
  };
};

const justifyCopyPosition = (copyPosition: CopyPosition): string => {
  switch (copyPosition) {
    case CopyPosition.Center:
      return 'sm:justify-center';
    case CopyPosition.Bottom:
    default:
      return 'sm:justify-end';
  }
};

const MediaCopy = ({
  sys,
  sectionName,
  variant,
  copy,
  sliderEntryCollection,
  color,
  containedColor,
  image,
  aspectRatio,
  headline,
  headlineEyebrow, //To be deprecated
  copyPosition,
  cta,
  hyperlinkedImagesCollection,
  alignment,
  mobileAlignment,
  notificationOverlay,
  notificationOverlayDesktopPosition,
  topRightBadge,
  margins,
  mobileCopyJustification,
  eyebrowText,
  eyebrowAlternateLogo,
  eyebrowChimeLogo,
  video,
}: MediaCopyProps) => {
  const { current, setApi, scrollTo } = useCarousel();
  variant = variant ?? MediaCopyVariants.BASE;
  const isContainedBackground = variant === MediaCopyVariants.CONTAINED_BACKGROUND;
  const copyItems =
    variant === MediaCopyVariants.COPY_SLIDER
      ? (sliderEntryCollection?.items ?? []).filter(Boolean).map(item => item.content)
      : copy
        ? [copy]
        : [];
  const useNewEyebrowFields = eyebrowText != null || eyebrowAlternateLogo != null || eyebrowChimeLogo === true;

  const sectionProps = getInnerOuterSectionProps(isContainedBackground, color, containedColor, margins, sectionName);

  // Validate video has a playable source (uploaded asset or external URL)
  const hasPlayableVideo = video != null && (video.video != null || video.videoUrl != null);

  // Fallback support for modules without notificationOverlayDesktopPosition selection
  if (!notificationOverlayDesktopPosition) {
    notificationOverlayDesktopPosition =
      alignment === Alignment.Right
        ? NotificationOverlayDesktopPosition.TopLeft
        : NotificationOverlayDesktopPosition.TopRight;
  }

  const content = (
    <section {...sectionProps.inner}>
      <div
        className={cn('content-wrapper', mobileCopyJustification === CopyJustification.Center && 'max-sm:text-center')}>
        {/* flex gap is standard 24px + additional 113px */}
        <div
          className={cn(
            'flex flex-col sm:gap-[137px]',
            !notificationOverlay?.display && 'gap-6',
            `${getAlignment(alignment, mobileAlignment)}`,
          )}>
          <div className="flex justify-center items-center w-full sm:w-1/2">
            <div className={cn('flex relative z-0', { 'w-full': hasPlayableVideo })}>
              {hasPlayableVideo ? (
                <VideoPlayer
                  video={video}
                  testId="media-copy-video-player"
                  {...liveUpdateAttrs(video.sys?.id, 'video')}
                  classNames={{
                    wrapper: 'w-full h-full rounded-[20px] overflow-hidden',
                    player: 'object-cover',
                  }}>
                  <VideoPreview image={video.previewImage} showPreviewOnEnd />
                  <VideoControls hideOnVideoStarted showOnVideoEnd position="center" />
                </VideoPlayer>
              ) : (
                <Media
                  media={image}
                  {...liveUpdateAttrs(image?.sys?.id, 'image')}
                  sizes="(max-width: 750px) 100vw, 610px"
                  aspectRatio={aspectRatio}
                />
              )}

              {notificationOverlay?.display && (
                <NotificationOverlay
                  description={notificationOverlay.image.description}
                  sys={notificationOverlay.sys}
                  url={notificationOverlay.image.url}
                  fileName={notificationOverlay.image.fileName}
                  notificationOverlayDesktopPosition={notificationOverlayDesktopPosition}
                />
              )}

              {topRightBadge && (
                <BadgeOverlay
                  description={topRightBadge.image.description}
                  sys={topRightBadge.sys}
                  url={topRightBadge.image.url}
                  fileName={topRightBadge.image.fileName}
                />
              )}
            </div>
          </div>
          <div
            className={`flex flex-col w-full sm:w-1/2 sm:mt-6 ${calculateOverlayMargin(notificationOverlay?.display)} ${justifyCopyPosition(copyPosition as CopyPosition)}`}>
            {!useNewEyebrowFields && (
              <div className="flex flex-row items-end space-between top empty:hidden mb-4 sm:mb-6">
                {headlineEyebrow?.textRte && (
                  <div
                    className="media-copy-eyebrow text-eyebrow font-sans text-grey-200 in-cw-dark-colorways:text-lettuce"
                    {...liveUpdateAttrs(headlineEyebrow.sys?.id, 'textRte')}>
                    <RichTextCopy copy={headlineEyebrow.textRte} />
                  </div>
                )}
                {headlineEyebrow && headlineEyebrow?.alternateLogo?.url && (
                  <div>
                    <Media className="rounded-none" media={headlineEyebrow.alternateLogo} />
                  </div>
                )}
              </div>
            )}
            {useNewEyebrowFields && (
              <Eyebrow
                sys={sys}
                eyebrowAlternateLogo={eyebrowAlternateLogo}
                eyebrowText={eyebrowText}
                eyebrowChimeLogo={eyebrowChimeLogo}
                color={color}
                className={cn(
                  'sm:mb-6 text-grey-200 in-cw-dark-colorways:text-lettuce',
                  mobileCopyJustification === CopyJustification.Center && 'max-sm:mx-auto',
                )}
              />
            )}
            {headline && (
              <div
                className={cn(
                  'font-sans font-semibold sm:w-full text-heading-md md:text-heading-lg',
                  isContainedBackground && 'leading-none',
                )}
                {...liveUpdateAttrs(sys?.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            {copyItems && copyItems.length === 1 && (
              <div className="font-serif font-body-serif text-lg mt-6">
                <RichTextCopy copy={copyItems[0]} />
              </div>
            )}
            {copyItems && copyItems.length > 1 && (
              <AutoPlayCarousel
                className="flex flex-col gap-8 md:gap-[50px]"
                setApi={setApi}
                plugins={[AutoHeight()]}
                opts={{ align: 'start', slidesToScroll: 1, containScroll: false, watchDrag: copyItems.length > 1 }}
                playOnInit={true}
                delay={5000}
                data-testid="media-copy-carousel">
                <CarouselContent className={`w-full`} overflowVisible={OverflowVisibleSettings.None}>
                  <div className="flex space-x-8">
                    {copyItems?.map((item, i) => (
                      <MediaCopyCopyItem key={i} alignment={alignment} copy={item!} />
                    ))}
                  </div>
                </CarouselContent>
                <CarouselFooter
                  totalItems={copyItems?.length}
                  current={current}
                  scrollTo={scrollTo}
                  className="flex sm:justify-start"
                  isTimed={true}
                  timeDuration={5}
                />
              </AutoPlayCarousel>
            )}
            <div className="empty:hidden mt-8 md:mt-[50px]">
              {isValidCta(cta) && (
                <div className="w-full md:w-96">
                  <CTA cta={cta} />
                </div>
              )}
              {hyperlinkedImagesCollection?.items && hyperlinkedImagesCollection?.items?.length > 0 && (
                <div
                  className={cn(
                    'flex flex-row flex-wrap gap-5',
                    mobileCopyJustification === CopyJustification.Center && 'max-sm:justify-center',
                  )}>
                  {hyperlinkedImagesCollection?.items?.map((item, index) => (
                    <HyperlinkedMedia key={index} link={item.link} image={item.image} targetHeight={40} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return isContainedBackground ? (
    <div {...sectionProps.outer}>
      <div className="content-wrapper">{content}</div>
    </div>
  ) : (
    content
  );
};

export default MediaCopy;
